import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';

export const interestOptions = [
  { 
    id: 'maintenance_plans', 
    title: 'Staying on top of maintenance',
    description: 'Reminders, schedules, and nothing slipping through the cracks.',
    capabilities: ['maintenance_plans', 'reminders']
  },
  { 
    id: 'home_manager', 
    title: 'Having one person who knows my home',
    description: 'A dedicated home manager who handles things for me.',
    capabilities: ['home_manager']
  },
  { 
    id: 'service_provider_management', 
    title: 'Managing repairs and service pros',
    description: 'Finding, coordinating, and keeping track of service providers.',
    capabilities: ['service_provider_management']
  },
  { 
    id: 'prioritizing_projects', 
    title: 'Planning and prioritizing projects',
    description: 'Knowing what to do now vs. what can wait.',
    capabilities: ['prioritizing_projects']
  },
  { 
    id: 'organization', 
    title: 'Keeping everything organized',
    description: 'Warranties, quotes, receipts, and records in one place.',
    capabilities: ['warranty_tracking', 'digital_records', 'quotes_estimates']
  },
  { 
    id: 'all_of_it', 
    title: 'Honestly… all of it',
    description: 'I want home management handled for me.',
    capabilities: ['all'],
    isHighlighted: true
  },
];

interface InterestsStepProps {
  interests: string[];
  onInterestToggle: (interest: string) => void;
}

export const InterestsStep = ({
  interests,
  onInterestToggle
}: InterestsStepProps) => {
  return (
    <View style={styles.stepContainer}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>What do you want off your plate right now?</Text>
        <Text style={styles.stepSubtitle}>
          We'll tailor everything around this. You can change it anytime.
        </Text>
      </View>
      <View style={styles.interestsList}>
        {interestOptions.map((option) => {
          const isSelected = interests.includes(option.id);
          const isHighlighted = option.isHighlighted;
          
          return (
            <Pressable
              key={option.id}
              onPress={() => onInterestToggle(option.id)}
              style={[
                styles.interestCard,
                isSelected && styles.interestCardSelected,
                isHighlighted && styles.interestCardHighlighted,
                isSelected && isHighlighted && styles.interestCardHighlightedSelected
              ]}
            >
              <View style={styles.checkboxContainer}>
                <View style={[
                  styles.checkbox,
                  isSelected && styles.checkboxSelected
                ]}>
                  {isSelected && (
                    <Text style={styles.checkmark}>✓</Text>
                  )}
                </View>
              </View>
              <View style={styles.cardContent}>
                <View style={styles.cardHeader}>
                  <Text style={[
                    styles.cardTitle,
                    isSelected && styles.cardTitleSelected,
                    isHighlighted && !isSelected && styles.cardTitleHighlighted
                  ]}>
                    {option.title}
                    {isHighlighted && ' ⭐'}
                  </Text>
                </View>
                <Text style={[
                  styles.cardDescription,
                  isSelected && styles.cardDescriptionSelected
                ]}>
                  {option.description}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    gap: 32,
  },
  stepHeader: {
    gap: 8,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#4b5563',
  },
  interestsList: {
    gap: 12,
  },
  interestCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderColor: '#e5e7eb',
    alignItems: 'flex-start',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    }),
  },
  interestCardSelected: {
    borderColor: '#000000',
    backgroundColor: '#000000',
  },
  interestCardHighlighted: {
    borderColor: '#fbbf24',
    backgroundColor: '#fffbeb',
  },
  interestCardHighlightedSelected: {
    borderColor: '#000000',
    backgroundColor: '#000000',
  },
  checkboxContainer: {
    marginRight: 12,
    marginTop: 2,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#9ca3af',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  checkboxSelected: {
    borderColor: '#ffffff',
    backgroundColor: 'transparent',
  },
  checkmark: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  cardContent: {
    flex: 1,
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  cardTitleSelected: {
    color: '#ffffff',
  },
  cardTitleHighlighted: {
    color: '#92400e',
  },
  cardDescription: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  cardDescriptionSelected: {
    color: '#d1d5db',
  },
});
